import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * PageSelector Component
 * Allows users to select specific pages from split documents
 */
export default function PageSelector({
  pages,
  selectedPages,
  onSelectionChange,
  stats,
  onAutoSelect,
  className = ''
}) {
  const [previewPage, setPreviewPage] = useState(null);
  const [searchKeywords, setSearchKeywords] = useState('');

  // Toggle single page selection
  const togglePage = useCallback((pageIndex) => {
    const newSelection = selectedPages.includes(pageIndex)
      ? selectedPages.filter(i => i !== pageIndex)
      : [...selectedPages, pageIndex].sort((a, b) => a - b);
    onSelectionChange(newSelection);
  }, [selectedPages, onSelectionChange]);

  // Select all pages
  const selectAll = useCallback(() => {
    onSelectionChange(pages.map((_, i) => i));
  }, [pages, onSelectionChange]);

  // Clear selection
  const clearSelection = useCallback(() => {
    onSelectionChange([]);
  }, [onSelectionChange]);

  // Select range
  const selectRange = useCallback((start, end) => {
    const range = [];
    for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
      range.push(i);
    }
    const newSelection = [...new Set([...selectedPages, ...range])].sort((a, b) => a - b);
    onSelectionChange(newSelection);
  }, [selectedPages, onSelectionChange]);

  // Handle auto-select with keywords
  const handleAutoSelect = useCallback(() => {
    if (onAutoSelect && searchKeywords.trim()) {
      onAutoSelect(searchKeywords);
      setSearchKeywords('');
    }
  }, [onAutoSelect, searchKeywords]);

  // Format character count
  const formatChars = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Header with stats */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
            📄 Seiten auswählen
            <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
              {selectedPages.length}/{pages.length}
            </span>
          </h3>
          
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="text-xs px-2 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded"
            >
              Alle
            </button>
            <button
              onClick={clearSelection}
              className="text-xs px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              Keine
            </button>
          </div>
        </div>

        {/* Stats bar */}
        {stats && (
          <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span title="Ausgewählte Zeichen">
              📝 {formatChars(stats.selectedChars)} / {formatChars(stats.totalChars)} chars
            </span>
            <span title="Geschätzte Tokens">
              🎯 ~{stats.estimatedTokens.toLocaleString()} tokens
            </span>
            <span className="text-primary-600 dark:text-primary-400">
              {stats.percentage}% ausgewählt
            </span>
            {stats.hasGaps && (
              <span className="text-warning-600 dark:text-warning-400" title="Es gibt Lücken zwischen ausgewählten Seiten">
                ⚠️ Lücken
              </span>
            )}
          </div>
        )}

        {/* Auto-select by keywords */}
        {onAutoSelect && (
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={searchKeywords}
              onChange={(e) => setSearchKeywords(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAutoSelect()}
              placeholder="Keywords für Auto-Auswahl..."
              className="flex-1 text-sm px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
            />
            <button
              onClick={handleAutoSelect}
              disabled={!searchKeywords.trim()}
              className="text-sm px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 disabled:opacity-50"
            >
              🔍 Suchen
            </button>
          </div>
        )}
      </div>

      {/* Page grid */}
      <div className="p-4 grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2 max-h-48 overflow-y-auto">
        {pages.map((page, index) => {
          const isSelected = selectedPages.includes(index);
          const isConsecutive = selectedPages.includes(index - 1) || selectedPages.includes(index + 1);
          
          return (
            <button
              key={index}
              onClick={() => togglePage(index)}
              onMouseEnter={() => setPreviewPage(index)}
              onMouseLeave={() => setPreviewPage(null)}
              className={`
                relative aspect-square rounded-lg text-sm font-medium transition-all
                ${isSelected
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }
                ${isSelected && isConsecutive ? 'ring-2 ring-primary-300 dark:ring-primary-500' : ''}
              `}
              title={`Seite ${page.pageNumber}: ${formatChars(page.charCount)} Zeichen`}
            >
              {page.pageNumber}
            </button>
          );
        })}
      </div>

      {/* Page preview on hover */}
      {previewPage !== null && pages[previewPage] && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Vorschau Seite {pages[previewPage].pageNumber}
            </span>
            <span className="text-xs text-gray-400">
              {formatChars(pages[previewPage].charCount)} chars
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-3 font-mono">
            {pages[previewPage].content.slice(0, 300)}
            {pages[previewPage].content.length > 300 && '...'}
          </p>
        </div>
      )}

      {/* Quick range selector */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 self-center">Schnellauswahl:</span>
          <button
            onClick={() => selectRange(0, Math.min(2, pages.length - 1))}
            className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Erste 3
          </button>
          <button
            onClick={() => selectRange(Math.max(0, pages.length - 3), pages.length - 1)}
            className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Letzte 3
          </button>
          <button
            onClick={() => {
              const evens = pages.map((_, i) => i).filter(i => i % 2 === 0);
              onSelectionChange(evens);
            }}
            className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Gerade
          </button>
          <button
            onClick={() => {
              const odds = pages.map((_, i) => i).filter(i => i % 2 === 1);
              onSelectionChange(odds);
            }}
            className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Ungerade
          </button>
        </div>
      </div>
    </div>
  );
}

PageSelector.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.shape({
    pageNumber: PropTypes.number.isRequired,
    content: PropTypes.string.isRequired,
    charCount: PropTypes.number.isRequired
  })).isRequired,
  selectedPages: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  stats: PropTypes.shape({
    totalPages: PropTypes.number,
    selectedPages: PropTypes.number,
    totalChars: PropTypes.number,
    selectedChars: PropTypes.number,
    estimatedTokens: PropTypes.number,
    hasGaps: PropTypes.bool,
    percentage: PropTypes.number
  }),
  onAutoSelect: PropTypes.func,
  className: PropTypes.string
};
